<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div v-if="!hideHeader" class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Pension</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <div v-if="isLoading" class="px-6 py-12 flex justify-center items-center">
      <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <form v-else @submit.prevent="save" class="px-6 pt-2 pb-6">
      <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] items-end gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Employee Pension Contribution %</label>
          <input
            v-model="form.employeeContribution"
            type="text"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Employee contribution"
            required
          >
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Company Pension Contribution %</label>
          <input
            v-model="form.companyContribution"
            type="text"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Company contribution"
            required
          >
        </div>

        <button
          type="submit"
          :disabled="!canEditPension || isSaving"
          class="min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <div
            v-if="isSaving"
            class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Save</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { computed, defineProps, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const props = defineProps({
  apiBase: {
    type: String,
    default: '/finance'
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  },
  hideHeader: {
    type: Boolean,
    default: false
  },
  privilegeName: {
    type: String,
    default: 'Pension'
  }
})

const store = useStore()

const pensionApiPath = computed(() => `${props.apiBase}/pension`)

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canEditPension = computed(() => {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), 'Edit')
})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = reactive({
  employeeContribution: '',
  companyContribution: ''
})

const isLoading = ref(false)
const isSaving = ref(false)
const hasExisting = ref(false)

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

async function loadPension() {
  isLoading.value = true
  try {
    const res = await apiFetch(pensionApiPath.value)
    if (!res.ok) {
      hasExisting.value = false
      form.employeeContribution = ''
      form.companyContribution = ''
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load pension settings.', 'error')
      return
    }
    const data = await res.json().catch(() => null)
    if (!data || typeof data !== 'object') {
      hasExisting.value = false
      form.employeeContribution = ''
      form.companyContribution = ''
      return
    }
    hasExisting.value = true
    form.employeeContribution =
      data.employeeContribution != null ? String(data.employeeContribution) : ''
    form.companyContribution =
      data.companyContribution != null ? String(data.companyContribution) : ''
  } catch {
    hasExisting.value = false
    showToast('Failed to load pension settings.', 'error')
  } finally {
    isLoading.value = false
  }
}

async function save() {
  if (isSaving.value || isLoading.value) return
  if (!canEditPension.value) return

  const employeeContribution = String(form.employeeContribution || '').trim()
  const companyContribution = String(form.companyContribution || '').trim()
  if (!employeeContribution || !companyContribution) return

  const wasExisting = hasExisting.value
  isSaving.value = true
  try {
    const res = await apiFetch(pensionApiPath.value, {
      method: 'PUT',
      body: JSON.stringify({ employeeContribution, companyContribution })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save pension settings.', 'error')
      return
    }
    hasExisting.value = true
    showToast(
      wasExisting
        ? 'Pension settings updated successfully.'
        : 'Pension settings saved successfully.',
      'success'
    )
    await loadPension()
  } catch {
    showToast('Failed to save pension settings.', 'error')
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  loadPension()
})
</script>
